import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { SubscribeListType } from '../earlyWarning/emailComp/EmailTypes'
import { APIResponseAcknowledgement } from './APIAcknowledgeService';
import { convertToOffsetDateTime } from '../createWarningComp/CommonMethods';
import { warningMap } from '../createWarningComp/FixedDataSet';
import { DownIcon, UpIcon } from '../iconSet/IconSet';

type AcknowledgmentTablePropsType = {
    tableData: APIResponseAcknowledgement[];
}

const AcknowledgmentTable = (props: AcknowledgmentTablePropsType) => {
    const [selectedCodes, setSelectedCodes] = useState(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const [filteredData, setFilteredData] = useState<APIResponseAcknowledgement[]>(props.tableData)
    const [slicedData, setSlicedData] = useState<APIResponseAcknowledgement[][]>([])
    
    // true = A -> Z
    // false = Z -> A
    const [warningIdSort, setWarningIdSort] = useState<Boolean>(true)
    const warningIdSortHandle = () => {
        setWarningIdSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                const warningIdComparison = warningIdSort ? a.ewId.localeCompare(b.ewId) : b.ewId.localeCompare(a.ewId)
                return warningIdComparison
            })
        })
    }, [warningIdSort])

    const [warningTypeSort, setWarningTypeSort] = useState<Boolean>(true)
    const warningTypeSortHandle = () => {
        setWarningTypeSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                const warningTypeComparison = warningTypeSort ? a.type.localeCompare(b.type) : b.type.localeCompare(a.type)
                return warningTypeComparison
            })
        })
    }, [warningTypeSort])

    const [warningAreaSort, setWarningAreaSort] = useState<Boolean>(true)
    const warningAreaSortHandle = () => {
        setWarningAreaSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                const warningAreaComparison = warningAreaSort ? a.clientGn.localeCompare(b.clientGn) : b.clientGn.localeCompare(a.clientGn)
                return warningAreaComparison
            })
        })
    }, [warningAreaSort])

    const [warningTimeSort, setWarningTimeSort] = useState<Boolean>(true)
    const warningTimeSortHandle = () => {
        setWarningTimeSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                const warningTimeComparison = warningTimeSort ? a.timestamp.localeCompare(b.timestamp) : b.timestamp.localeCompare(a.timestamp)
                return warningTimeComparison
            })
        })
    }, [warningTimeSort])

    useEffect(() => {
        setFilteredData(props.tableData)
    }, [props.tableData])

    const paginationList = (data: APIResponseAcknowledgement[], size: number): APIResponseAcknowledgement[][] => {
        const result: APIResponseAcknowledgement[][] = []
    
        if (size >= data.length) {
            return [data]
        }
    
        const length = data.length
    
        let start = 0
    
        let isOk = true
        while(isOk) {
            if (start < length) {
                result.push(data.slice(start, start + size))
                start += size
            } else {
                result.push(data.slice(start, -1))
                isOk = false
            }
        }
        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
            }
           
        } else if (side === "prev") {
            if (0 <= selectedPage - 1){
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    // Handle slice count changed
    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    // handle search text change
    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === '') {
            setFilteredData(props.tableData)
        } else {
            setFilteredData(prevState => prevState.filter(item => item.ewId.toLowerCase().includes(event.target.value.toLowerCase()) || item.clientId.toLowerCase().includes(event.target.value.toLowerCase())))
        }
    }

    useEffect(() => {
        if (rowNumber.current){
            setSlicedData(prevState => paginationList(filteredData, sliceCount))
        }
    }, [sliceCount, filteredData])

    return (
        <div className='subTable-wrapper' style={{width: '100%'}}>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} defaultValue={10} min={10} onChange={(e) => handleSliceCountChange(e)}/> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here'/></span>
                </div>
            </div>
            <div className='table-body'>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{gridTemplateColumns: 'repeat(4, 1fr)', width: '100%'}}>
                        <span onClick={(e) => warningIdSortHandle()}>
                            Early Warning Id
                            <div>
                                {warningIdSort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}
                            </div>
                        </span>
                        <span onClick={(e) => warningTypeSortHandle()}>
                            Early Warning Type
                            <div>
                                {warningTypeSort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}
                            </div>
                        </span>
                        <span onClick={(e) => warningAreaSortHandle()}>
                            Area
                            <div>
                                {warningAreaSort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}
                            </div>
                        </span>
                        <span onClick={(e) => warningTimeSortHandle()}>
                            Time
                            <div>
                                {warningTimeSort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}
                            </div>
                        </span>
                    </div>
                </div>
                {props.tableData.length > 0 ? (
                    slicedData[selectedPage] && slicedData[selectedPage].map((row, index) => (
                        <div key={index} className='table-wrapper'>
                            <div className='table-row' style={{gridTemplateColumns: 'repeat(4, 1fr)', width: '100%'}}>
                                <span>{row.ewId}</span>
                                <span>{warningMap.get(row.type)}</span>
                                <span>{row.name}</span>
                                <span>{convertToOffsetDateTime(row.timestamp)}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className='empty-table-data-wrapper'>
                        <span>No any data available</span>
                    </div>
                )}
                
            </div>
            
            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{marginBottom: "0.5rem"}} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{marginBottom: "0.5rem"}} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
        </div>
    )
}

export default AcknowledgmentTable